import { Component, inject, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { TranslateModule } from '@ngx-translate/core'
import { combineLatest, filter, map } from 'rxjs'
import { ToastService } from '../toast.service'
import { Router } from '@angular/router'
import { UsersService, User } from '../users.service'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { setSeconds } from 'date-fns'
import { CallsService, UploadCallData } from '../calls.service'
import { StepProgressComponent } from '../common/step-progress/step-progress.component'
import { DatetimePickerComponent } from '../common/datetime-picker/datetime-picker.component'
import { SelectionComponent } from '../users-selection/selection.component'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { constructNameForUser } from '../utils'

@Component({
  selector: 'app-upload-call-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ToolbarComponent,
    StepProgressComponent,
    DatetimePickerComponent,
    SelectionComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './upload-call-page.component.html',
  styleUrl: './upload-call-page.component.scss',
})
export class UploadCallPageComponent implements OnInit {
  private toastService = inject(ToastService)
  private router = inject(Router)
  private usersService = inject(UsersService)
  private callsService = inject(CallsService)

  currentUserQuery$ = this.usersService.getCurrentUser()
  organizationUsers$ = this.usersService.getOrganizationUsers()

  organizationUsersWithoutCurrentUser$ = combineLatest([this.currentUserQuery$, this.organizationUsers$]).pipe(
    map(([currentUserResult, organizationUsersResult]) => {
      if (currentUserResult.isSuccess && organizationUsersResult.isSuccess) {
        const currentUser = currentUserResult.data
        const filteredUsers = organizationUsersResult.data!.filter((user) => user.id !== currentUser!.id)
        return { isSuccess: true, data: filteredUsers }
      } else {
        return { isSuccess: false, data: [] }
      }
    })
  )

  stepLabels = [
    'MEETING_SETUP.STEPS.MEETING_DETAILS',
    'MEETING_SETUP.STEPS.TEMPLATE_SOURCE',
    'MEETING_SETUP.STEPS.REVIEW',
    'MEETING_SETUP.STEPS.UPLOAD_RECORDING',
  ]

  data: UploadCallData = {
    name: '',
    date: setSeconds(new Date(), 0),
    userIds: [],
  }

  uploadCallMutation = this.callsService.uploadCall()

  get isCreateButtonDisabled(): boolean {
    return this.data.name.trim() === ''
  }

  ngOnInit() {
    this.uploadCallMutation.result$
      .pipe(
        filter((result) => result.isSuccess),
        map((result) => result.data)
      )
      .subscribe((call) => {
        this.toastService.show('UPLOAD_CALL.IMPORT_SUCCESSFUL_NOTIFICATION')
        this.router.navigate(['/app', 'meetings', call!.id])
      })
  }

  addFullNameToUserList(users: User[]) {
    return users.map((user) => {
      return {
        ...user,
        fullName: constructNameForUser(user),
      }
    })
  }

  onUserSelectionChange(users: User[]) {
    this.data.userIds = users.map((user) => user.id)
  }

  onCreateCallClicked() {
    this.uploadCallMutation.mutate(this.data)
  }
}
